<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12" sm="6">
        <h1>Rate Cards</h1>
        <p class="text-body2 text-medium-emphasis">Day &amp; hourly rates by role</p>
      </v-col>
      <v-col v-if="orgStore.isManagerPlus" cols="12" sm="6" class="text-right">
        <v-btn color="primary" @click="openCreate">
          <v-icon start>mdi-plus</v-icon>
          New Rate Card
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" closable @click:close="error = null">{{ error }}</v-alert>

    <v-row v-if="isLoading">
      <v-col cols="12"><v-skeleton-loader type="table-tbody" /></v-col>
    </v-row>

    <v-row v-else-if="rateCards.length === 0">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-12">
            <v-icon size="48" class="text-medium-emphasis mb-4">mdi-card-text-outline</v-icon>
            <p class="text-h6">No rate cards yet</p>
            <p class="text-body2 text-medium-emphasis">Create one to fill position rates automatically</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col v-for="card in rateCards" :key="card.id" cols="12" md="6">
        <v-card class="h-100">
          <v-card-item>
            <template #title>{{ card.name }}</template>
            <template v-if="orgStore.isManagerPlus" #append>
              <v-btn icon="mdi-pencil" size="small" variant="text" color="primary" @click="openEdit(card)" />
              <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="removeCard(card.id)" />
            </template>
          </v-card-item>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>Role</th>
                  <th class="text-right">Bill Day</th>
                  <th class="text-right">Bill Hourly</th>
                  <th class="text-right">Pay Day</th>
                  <th class="text-right">Pay Hourly</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, role) in card.roles" :key="role">
                  <td>{{ role }}</td>
                  <td class="text-right">${{ r.dayRate.amount }}</td>
                  <td class="text-right">${{ r.hourlyRate.amount }}</td>
                  <td class="text-right text-medium-emphasis">{{ r.payDayRate ? `$${r.payDayRate.amount}` : '—' }}</td>
                  <td class="text-right text-medium-emphasis">{{ r.payHourlyRate ? `$${r.payHourlyRate.amount}` : '—' }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showDialog" width="720">
      <v-card>
        <v-card-title>{{ editingId ? 'Edit Rate Card' : 'New Rate Card' }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="save">
            <v-text-field v-model="form.name" label="Name" required class="mb-4" />

            <div v-for="(row, i) in form.rows" :key="i" class="mb-3">
              <v-row dense align="center">
                <v-col cols="12" sm="4">
                  <v-text-field v-model="row.role" label="Role" density="compact" hide-details />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model.number="row.dayRate" label="Bill Day $" type="number" density="compact" hide-details />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model.number="row.hourlyRate" label="Bill Hourly $" type="number" density="compact" hide-details />
                </v-col>
                <v-col cols="12" sm="2" class="text-right">
                  <v-btn icon="mdi-close" size="x-small" variant="text" @click="form.rows.splice(i, 1)" />
                </v-col>
              </v-row>
              <v-row dense align="center" class="mt-1">
                <v-col cols="12" sm="4" class="text-caption text-medium-emphasis">
                  Pay rate (defaults to bill rate above if left blank)
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model.number="row.payDayRate" label="Pay Day $" type="number" density="compact" hide-details />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model.number="row.payHourlyRate" label="Pay Hourly $" type="number" density="compact" hide-details />
                </v-col>
              </v-row>
              <v-expansion-panels class="mt-1" variant="accordion">
                <v-expansion-panel>
                  <v-expansion-panel-title>Advanced (OT / rest)</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-switch
                      v-model="row.useStandardTimekeeping"
                      label="Use standard timekeeping policy"
                      density="compact"
                      hide-details
                      class="mb-2"
                    />
                    <template v-if="!row.useStandardTimekeeping">
                      <v-row dense>
                        <v-col cols="4">
                          <v-text-field v-model.number="row.minHourlyBill" label="Min hrs billed" type="number" density="compact" hide-details />
                        </v-col>
                        <v-col cols="4">
                          <v-text-field v-model.number="row.restPeriodHrs" label="Rest period hrs" type="number" density="compact" hide-details />
                        </v-col>
                        <v-col cols="4">
                          <v-text-field v-model.number="row.otStartHrs" label="OT starts at hr" type="number" density="compact" hide-details />
                        </v-col>
                      </v-row>
                    </template>
                    <template v-else>
                      <p class="text-caption text-medium-emphasis mb-2">
                        Two-tier overtime, per-call minimums, rest-gap OT reset, and holiday billing — see
                        Settings for the org's recognized holidays. Fields blank below use the standard defaults.
                      </p>
                      <v-row dense>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="row.otStartHrs"
                            label="1st OT tier at hr (8 hourly / 10 day, default)"
                            type="number"
                            density="compact"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="row.otSecondStartHrs"
                            label="2nd OT tier at hr (12, default)"
                            type="number"
                            density="compact"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="row.otSecondMultiplier"
                            label="2nd OT tier multiplier (2.0x, default)"
                            type="number"
                            step="0.1"
                            density="compact"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="row.restPeriodHrs"
                            label="Rest period hrs (10, default)"
                            type="number"
                            density="compact"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="row.firstCallMinHrs"
                            label="First call min hrs (4, default)"
                            type="number"
                            density="compact"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="row.subsequentCallMinHrs"
                            label="Later call min hrs (2, default)"
                            type="number"
                            density="compact"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <v-btn size="small" variant="tonal" class="mb-4" @click="addRow">
              <v-icon start size="small">mdi-plus</v-icon>Add Role
            </v-btn>

            <div class="text-right">
              <v-btn text @click="showDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="saving">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import type { RateCard } from '@gigdriver/domain';
import { useRateCardsStore } from '../stores/rateCards';
import { useOrganizationStore } from '../stores/organization';

interface RoleRow {
  role: string;
  dayRate: number;
  hourlyRate: number;
  payDayRate?: number;
  payHourlyRate?: number;
  minHourlyBill?: number;
  restPeriodHrs?: number;
  otStartHrs?: number;
  useStandardTimekeeping?: boolean;
  otSecondStartHrs?: number;
  otSecondMultiplier?: number;
  firstCallMinHrs?: number;
  subsequentCallMinHrs?: number;
}

const route = useRoute();
const store = useRateCardsStore();
const orgStore = useOrganizationStore(); // coordinators read rate cards; only manager+ edit

const rateCards = computed(() => store.rateCards);
const isLoading = computed(() => store.isLoading);
const error = ref<string | null>(null);

const showDialog = ref(false);
const saving = ref(false);
const editingId = ref<string | null>(null);
const form = reactive<{ name: string; rows: RoleRow[] }>({ name: '', rows: [] });

const orgId = () => route.query.org as string;

onMounted(() => {
  if (orgId()) store.loadOrgRateCards(orgId());
});

function addRow() {
  form.rows.push({ role: '', dayRate: 0, hourlyRate: 0 });
}

function openCreate() {
  editingId.value = null;
  form.name = '';
  form.rows = [{ role: '', dayRate: 0, hourlyRate: 0 }];
  showDialog.value = true;
}

function openEdit(card: RateCard) {
  editingId.value = card.id;
  form.name = card.name;
  form.rows = Object.entries(card.roles).map(([role, r]) => ({
    role,
    dayRate: r.dayRate.amount,
    hourlyRate: r.hourlyRate.amount,
    payDayRate: r.payDayRate?.amount,
    payHourlyRate: r.payHourlyRate?.amount,
    minHourlyBill: r.minHourlyBill,
    restPeriodHrs: r.restPeriodHrs,
    otStartHrs: r.otStartHrs,
    useStandardTimekeeping: r.useStandardTimekeeping,
    otSecondStartHrs: r.otSecondStartHrs,
    otSecondMultiplier: r.otSecondMultiplier,
    firstCallMinHrs: r.firstCallMinHrs,
    subsequentCallMinHrs: r.subsequentCallMinHrs,
  }));
  if (form.rows.length === 0) form.rows.push({ role: '', dayRate: 0, hourlyRate: 0 });
  showDialog.value = true;
}

async function save() {
  try {
    saving.value = true;
    error.value = null;
    const roles: RateCard['roles'] = {};
    for (const r of form.rows) {
      const role = r.role.trim();
      if (!role) continue;
      roles[role] = {
        dayRate: { amount: r.dayRate || 0, currency: 'USD' },
        hourlyRate: { amount: r.hourlyRate || 0, currency: 'USD' },
        payDayRate: r.payDayRate ? { amount: r.payDayRate, currency: 'USD' } : undefined,
        payHourlyRate: r.payHourlyRate ? { amount: r.payHourlyRate, currency: 'USD' } : undefined,
        minHourlyBill: r.minHourlyBill || undefined,
        restPeriodHrs: r.restPeriodHrs || undefined,
        otStartHrs: r.otStartHrs || undefined,
        useStandardTimekeeping: r.useStandardTimekeeping || undefined,
        otSecondStartHrs: r.otSecondStartHrs || undefined,
        otSecondMultiplier: r.otSecondMultiplier || undefined,
        firstCallMinHrs: r.firstCallMinHrs || undefined,
        subsequentCallMinHrs: r.subsequentCallMinHrs || undefined,
      };
    }
    const payload = { name: form.name, roles };
    if (editingId.value) {
      await store.updateRateCard(orgId(), editingId.value, payload);
    } else {
      await store.createRateCard(orgId(), payload);
    }
    showDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to save';
  } finally {
    saving.value = false;
  }
}

async function removeCard(id: string) {
  if (!confirm('Delete this rate card?')) return;
  try {
    error.value = null;
    await store.deleteRateCard(orgId(), id);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to delete';
  }
}
</script>
